import { useLocation } from "react-router-dom";
import { StyledFieldsStructure } from "./AccountFieldsStructure.styled"
import { useEffect, useState } from "react";
import { AccountStructure, AccountStructureSchema } from "../../../models/accounts-structure";
import { TicketHebType } from "../../../models/types";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, CircularProgress, IconButton, MenuItem, Select, TextField } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { z } from "zod";
import { ErrorMessage } from "@hookform/error-message";
import { checkInputValue, compareObjects, sortByLocation } from "@shared/lib/utils";
import { toast } from 'react-toastify';
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors"
import { Params, SelectTable } from "../../../models/types";
import { setParams } from "./AccountFieldsStructure.utils";
import { accountsTables, inputTypes } from "./AccountFieldsStructure.data";
import { Table } from "../../../models/table";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { useAddCustomAccountStructureMutation, useEditAccountStructureMutation, useGetAccountStructureQuery } from "../api/accountsStructure.api";
import { useGetSystemTablesQuery } from "@features/tables/api/systemTables.api";
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactNode } from "react";



interface SortableFieldRowProps {
    id: string;
    position: number;
    children: ReactNode;
}

/**
 * A draggable table row. Dropping it elsewhere triggers AccountFieldsStructure's handleDragEnd,
 * which renumbers every row's `location` from 1 (topmost) to the field count (bottommost).
 */
function SortableFieldRow({ id, position, children }: SortableFieldRowProps) {

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <tr
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
        >
            <td>
                <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>
                {position}
            </td>
            {children}
        </tr>
    );
}



function AccountFieldsStructure() {

    const location = useLocation();
    const { showBoundary } = useErrorBoundary();

    const params: Params = setParams(location.pathname);

    const { data: systemTables } = useGetSystemTablesQuery();
    const { data: accountStructure, refetch: refetchAccountStructure } = useGetAccountStructureQuery(params.ticketHebType);

    const [editAccountStructure] = useEditAccountStructureMutation();
    const [addCustomAccountStructure] = useAddCustomAccountStructureMutation();

    const [tables, setTables] = useState<SelectTable[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [accountStructureCopy, setAccountStructureCopy] = useState<AccountStructure[]>();


    // account structures form

    const accountStructureForm = useForm<{ structures: AccountStructure[] }>({
        resolver: zodResolver(z.object({ structures: z.array(AccountStructureSchema) })),
        defaultValues: {
            structures: undefined
        }
    });

    const accountStructureFieldsArray = useFieldArray({
        name: 'structures',
        control: accountStructureForm.control
    });

    const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));



    // ************************************************************************************************************
    //  use effects
    // ************************************************************************************************************

    useEffect(()=>{
        if(accountStructure){
            const orderedAccountStructure = sortByLocation(accountStructure);
            setAccountStructureCopy(orderedAccountStructure);
            accountStructureForm.reset({ structures: orderedAccountStructure });
        }
    },[accountStructure]);


    useEffect(()=>{
        if(systemTables){
            setTable(systemTables,accountsTables);
        }
    },[systemTables]);



    useEffect(()=>{
        if(tables && accountStructureCopy){
            setIsLoading(false);
        }
    },[tables, accountStructureCopy]);



    // ************************************************************************************************************
    //  component functions
    // ************************************************************************************************************



    /**
     * combine system and accounts tables to one select table and update the state.
     * @param systemTables 
     * @param accountsTables 
     */
    function setTable(systemTables: Table[], accountsTables: SelectTable[]){

        const selectSystemTables: SelectTable[] = (systemTables ?? []).map(table => ({
            value: table.index,
            name: table.name ?? ""
        }));

        setTables([...selectSystemTables, ...accountsTables]);
    }



    /**
     * Saves structure form data
     */
    function saveTable() {
        accountStructureForm.handleSubmit(onSubmitAccountStructure)();
    }



    /**
     * Saves only the modified structures by comparing them with the last saved copy.
     * Shows a success toast.
     */
    const onSubmitAccountStructure: SubmitHandler<{ structures: AccountStructure[] }> = async (data) => {

        if (accountStructureCopy) {
            try {
                setIsSaving(true);

                for (const [index, structure] of data.structures.entries()) {
                    if (!compareObjects(structure, accountStructureCopy[index])) {
                        await editAccountStructure(structure);
                    }
                };

                toast.success("השינויים נשמרו");
            } 
            
            catch (error) {
                handleErrorWithBoundary(error, showBoundary);
            }

            finally {
                setIsSaving(false); // Always reset loading state
            }
        }

    }

    // ************************************************************************************************************


    /**
     * add new account custom structure
     * @param accountHebType 
     */
    async function addCustomField(ticketHebType: TicketHebType){

        try {
            const highestCustomField = (accountStructureCopy ?? []).reduce((max, structure) => {
                const current = structure.custom_field ?? 0;
                return current > max ? current : max;
            }, 0);
            const customFieldNumber = highestCustomField + 1;

            await addCustomAccountStructure({
                account_type: ticketHebType,
                account_title: "מותאם אישית",
                required: false,
                show_form: true,
                show_browse: false,
                field_name: `custom_data.${customFieldNumber - 1}.field_value`,
                field_heb_name: `מותאם אישית ${customFieldNumber}`,
                field_display_name: `מותאם אישית ${customFieldNumber}`,
                field_length: 5,
                field_type: 1,
                field_table: 0,
                custom_field: customFieldNumber,
                editable: true,
                location: null
            });

            await refetchAccountStructure();

        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }

    // ************************************************************************************************************


    /**
     * Moves the dragged field to its drop position and renumbers every field's `location`
     * (1 for the topmost row through the total field count for the bottommost row).
     */
    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = accountStructureFieldsArray.fields.findIndex((field) => field.id === active.id);
        const newIndex = accountStructureFieldsArray.fields.findIndex((field) => field.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        accountStructureFieldsArray.move(oldIndex, newIndex);

        const reorderedFields = arrayMove(accountStructureFieldsArray.fields, oldIndex, newIndex);
        reorderedFields.forEach((_, index) => {
            accountStructureForm.setValue(`structures.${index}.location`, index + 1, { shouldDirty: true });
        });
    }


    return (
        <StyledFieldsStructure>
            {isLoading && <CircularProgress color="inherit" sx={{ display: "block", margin: "auto", padding: 4 }} />}
            {!isLoading && (<>
                <div className="head">הגדרת מבנה {params.formType === "browse"? "דפדוף" : "טופס"} {params.ticketHebType}</div>
                <div className="table">
                    <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={accountStructureFieldsArray.fields.map((field) => field.id)}
                            strategy={verticalListSortingStrategy}
                        >
                    <table>
                        <thead>
                            <tr>
                                <th>מיקום</th>
                                <th>הצגה {params.formType === 'form' ? "בטופס" : "בטבלה"}</th>
                                <th>שם שדה</th>
                                <th>שם תצוגה</th>
                                <th>אורך</th>
                                <th>סוג</th>
                                <th>טבלה</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                tables && accountStructureFieldsArray.fields?.map((field, index) => (
                                    <SortableFieldRow key={field.id} id={field.id} position={index + 1}>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={params.formType === 'form' ? `structures.${index}.show_form` : `structures.${index}.show_browse`}

                                                render={({ field: { onChange, value } }) => (
                                                    <Checkbox
                                                        disabled={params.formType === 'form' && !!field.required}
                                                        onChange={onChange}
                                                        checked={!!value} />
                                                )}
                                            />
                                        </td>
                                        <td>{field.field_heb_name}</td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_display_name`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_display_name ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_display_name`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_length`}
                                                render={({ field: { onChange, value } }) => (
                                                    <TextField
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                        error={accountStructureForm?.formState?.errors?.structures?.[index]?.field_length ? true : false}
                                                        helperText={<ErrorMessage errors={accountStructureForm.formState.errors} name={`structures.${index}.field_length`} />}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_type`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        {inputTypes.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                control={accountStructureForm.control}
                                                name={`structures.${index}.field_table`}
                                                render={({ field: { onChange, value } }) => (
                                                    <Select
                                                        sx={{ minWidth: 150 }}
                                                        onChange={onChange} // send value to hook form
                                                        value={checkInputValue(value)}
                                                        size="small"
                                                    >
                                                        <MenuItem value={0}></MenuItem>
                                                        {tables?.map((obj, index) => (
                                                            <MenuItem key={index} value={obj.value}>{obj.value}. {obj.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </td>
                                    </SortableFieldRow>
                                ))
                            }


                        </tbody>
                    </table>
                        </SortableContext>
                    </DndContext>
                </div>
                <div className="buttons">
                    <Button variant='contained' onClick={saveTable} disabled={isSaving}>שמירה</Button>
                    <ReturnButton/>
                    <Button variant="outlined" onClick={()=>{addCustomField(params.ticketHebType)}}>הוספת שורה</Button>

                </div>


            </>)}


            {/* <DevTool control={accountStructureForm.control} /> */}

        </StyledFieldsStructure>
    )
}

export default AccountFieldsStructure